import { useCallback, useEffect, useMemo, useState } from "react";
import { useUIMessages } from "@convex-dev/agent/react";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import type { ChatMessage } from "@/components/ui/ChatPanel";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";

const INITIAL_MESSAGE_COUNT = 50;

export interface ThreadInfo {
  threadId: string;
  questionCount: number;
  firstQuestion: string;
  threadTitle: string | null;
  createdAt: number;
  lastMessageAt: number;
}

/**
 * Hook that manages multi-thread document chat:
 * - Lists all threads for a document
 * - Supports creating new threads, switching, and deleting
 * - Message history with streaming support
 */
export function useDocumentChat(documentId: Id<"documents"> | null) {
  const workspaceId = useWorkspaceId();

  // Track threadId from first message response (before query catches up)
  const [optimisticThreadId, setOptimisticThreadId] = useState<string | null>(
    null,
  );
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isNewThread, setIsNewThread] = useState(false);
  // Covers the entire window from "first message sent" to "messages arrive"
  // Prevents empty-state flicker while the agent thread is being created
  const [awaitingResponse, setAwaitingResponse] = useState(false);

  // 1. List all threads for this document+user
  const { data: threads = [], isLoading: isThreadsLoading } = useWorkspaceQuery(
    api.documents.queries.stats.listThreadsForDocument,
    { documentId: documentId ?? null },
  );

  // Derive active threadId:
  // - "new thread" mode -> null (will create on send)
  // - explicit selection -> selectedThreadId
  // - optimistic from just-sent message -> optimisticThreadId
  // - most recent from list -> threads[0]
  const threadId = isNewThread
    ? optimisticThreadId
    : (selectedThreadId ?? optimisticThreadId ?? threads[0]?.threadId ?? null);

  // 2. Fetch messages + streaming for the active thread
  const {
    results: rawMessages,
    status: paginationStatus,
    loadMore,
  } = useUIMessages(
    api.documents.queries.stats.listThreadMessages,
    threadId ? { threadId } : "skip",
    {
      initialNumItems: INITIAL_MESSAGE_COUNT,
      stream: true,
    },
  );

  // 3. Send messages via askQuestion mutation
  const askMutation = useApiMutation(api.documents.mutations.ai.askQuestion, {
    name: "document-chat-ask",
    silentSuccess: true,
    onSuccess: (rawData) => {
      const data = rawData;
      if (data.threadId) {
        setOptimisticThreadId(data.threadId);
        if (isNewThread) {
          setSelectedThreadId(data.threadId);
          setIsNewThread(false);
        }
      }
    },
  });

  // 4. Delete thread mutation
  const deleteMutation = useApiMutation(
    api.documents.mutations.ai.deleteThread,
    {
      name: "document-chat-delete-thread",
      successMessage: "Conversation deleted",
    },
  );

  // 5. Rename thread mutation
  const renameMutation = useApiMutation(
    api.documents.mutations.ai.renameThread,
    {
      name: "document-chat-rename-thread",
      successMessage: "Conversation renamed",
    },
  );

  // Map agent UIMessages to ChatPanel's ChatMessage format
  // Guard on threadId so stale rawMessages from a previous thread are ignored
  const messages: ChatMessage[] = useMemo(() => {
    if (!threadId || !rawMessages?.length) return [];

    return rawMessages
      .filter(
        (msg: { role: string }) =>
          msg.role === "user" || msg.role === "assistant",
      )
      .map(
        (msg: {
          key?: string;
          order: number;
          stepOrder: number;
          role: string;
          text?: string;
          _creationTime?: number;
        }) => ({
          id: msg.key ?? `${msg.order}-${msg.stepOrder}`,
          role: msg.role as "user" | "assistant",
          content: msg.text ?? "",
          timestamp: msg._creationTime
            ? new Date(msg._creationTime).toISOString()
            : undefined,
        }),
      );
  }, [rawMessages, threadId]);

  // Detect streaming — also guarded on threadId to avoid stale indicators
  const isStreaming = useMemo(() => {
    if (!threadId || !rawMessages?.length) return false;
    return rawMessages.some(
      (msg: { status?: string }) => msg.status === "streaming",
    );
  }, [rawMessages, threadId]);

  // Clear awaitingResponse once messages arrive for the active thread
  useEffect(() => {
    if (awaitingResponse && messages.length > 0) {
      setAwaitingResponse(false);
    }
  }, [awaitingResponse, messages.length]);

  const isFirstMessage = !threadId;

  const sendMessage = useCallback(
    async (text: string) => {
      if (!documentId || !text.trim()) return;

      // Show loading immediately for first message (no messages to display yet)
      if (!threadId) setAwaitingResponse(true);

      try {
        await askMutation.mutateAsync({
          question: text.trim(),
          documentIds: [documentId],
          ...(threadId ? { threadId } : {}),
          workspaceId,
        });
      } catch {
        setAwaitingResponse(false);
      }
    },
    [documentId, threadId, askMutation, workspaceId],
  );

  const startNewThread = useCallback(() => {
    setSelectedThreadId(null);
    setOptimisticThreadId(null);
    setIsNewThread(true);
    setAwaitingResponse(false);
  }, []);

  const selectThread = useCallback((id: string) => {
    setSelectedThreadId(id);
    setOptimisticThreadId(null);
    setIsNewThread(false);
    setAwaitingResponse(false);
  }, []);

  const deleteThread = useCallback(
    async (id: string) => {
      await deleteMutation.mutateAsync({ threadId: id, workspaceId });

      // If we deleted the active thread, fall back
      if (threadId === id) {
        setSelectedThreadId(null);
        setOptimisticThreadId(null);
        setIsNewThread(false);
      }
    },
    [deleteMutation, threadId, workspaceId],
  );

  const renameThread = useCallback(
    async (id: string, title: string) => {
      await renameMutation.mutateAsync({ threadId: id, title, workspaceId });
    },
    [renameMutation, workspaceId],
  );

  return {
    messages,
    sendMessage,
    isStreaming,
    isSending: askMutation.isPending,
    isLoading:
      isThreadsLoading ||
      awaitingResponse ||
      (!!threadId && paginationStatus === "LoadingFirstPage"),
    isFirstMessage,
    threadId,
    loadMore,
    hasMore: paginationStatus === "CanLoadMore",
    // Multi-thread
    threads,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
    isDeleting: deleteMutation.isPending,
    isRenaming: renameMutation.isPending,
  };
}
